import { useInView } from '@/hooks/useInView';

const pillars = [
  { num: '01', title: 'Fully Regulated', desc: 'Registered and inspected by the Care Inspectorate Scotland, fully compliant with all Scottish care standards.' },
  { num: '02', title: 'Person-Centred', desc: 'Every care plan is built around the individual: their history, preferences, and what matters most to them.' },
  { num: '03', title: 'Locally Rooted', desc: 'Based in Edinburgh, we know Scotland well. Our team understands the communities we work in.' },
  { num: '04', title: 'Consistent Carers', desc: 'The same face, every visit. We match carers carefully and keep that relationship stable.' },
];

export default function About() {
  const { ref: imgRef, inView: imgInView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  const { ref: textRef, inView: textInView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  const { ref: pillarsRef, inView: pillarsInView } = useInView<HTMLDivElement>({ threshold: 0.08 });

  return (
    <section id="about" className="overflow-hidden">

      {/* Full-bleed split: image left, text right */}
      <div className="flex flex-col lg:flex-row min-h-[70vh]">

        {/* Image panel */}
        <div
          ref={imgRef}
          className="w-full lg:w-5/12 h-[55vw] lg:h-auto relative overflow-hidden transition-all duration-1000 ease-out"
          style={{ opacity: imgInView ? 1 : 0 }}
        >
          <img
            src="https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Aligned Home Care carer with client"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#FAFAF8]/20" />
        </div>

        {/* Text panel */}
        <div
          ref={textRef}
          className="w-full lg:w-7/12 bg-[#FAFAF8] flex flex-col justify-center px-8 md:px-14 lg:px-20 py-16 lg:py-24 transition-all duration-700 ease-out"
          style={{ opacity: textInView ? 1 : 0, transform: textInView ? 'none' : 'translateX(24px)' }}
        >
          <p className="text-[#C0706A] text-[10px] tracking-[0.3em] uppercase font-semibold mb-7">
            Why Aligned Home Care
          </p>

          <h2
            className="text-[#2C3E50] font-bold leading-[1.15] mb-8"
            style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)' }}
          >
            We chose this work<br />
            because we care,{' '}
            <span className="text-[#C0706A] italic">and that shows.</span>
          </h2>

          <p className="text-[#2C3E50]/60 text-[15px] leading-relaxed mb-5 max-w-lg">
            Moving into care is a big decision. At Aligned Home Care, we work hard every day to make sure our clients feel safe, valued, and genuinely happy. From how we greet them each morning to how we support families through difficult moments.
          </p>
          <p className="text-[#2C3E50]/40 text-sm leading-relaxed mb-10 max-w-lg">
            We are registered and inspected by the Care Inspectorate Scotland. Every member of our team is PVG checked, trained, and chosen for their character as much as their qualifications.
          </p>

          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="self-start inline-flex items-center gap-2 text-sm font-bold text-[#2C3E50] border-b border-[#2C3E50]/40 pb-0.5 hover:text-[#C0706A] hover:border-[#C0706A] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Talk to us
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      {/* Pillars row — full width, no icons */}
      <div
        ref={pillarsRef}
        className="bg-[#2C3E50] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      >
        {pillars.map((p, i) => (
          <div
            key={p.num}
            className={`px-8 py-10 lg:py-12 border-b sm:border-b-0 border-white/10 ${i < 3 ? 'lg:border-r border-white/10' : ''} transition-all ease-out`}
            style={{
              opacity: pillarsInView ? 1 : 0,
              transform: pillarsInView ? 'none' : 'translateY(16px)',
              transitionDelay: `${i * 90}ms`,
              transitionDuration: '600ms',
            }}
          >
            <span className="block text-[#C0706A]/40 text-[10px] font-mono tracking-widest mb-5">{p.num}</span>
            <h3 className="text-white font-bold text-base mb-2">{p.title}</h3>
            <p className="text-white/40 text-sm leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>

    </section>
  );
}
